import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from 'utils/cn';
import { useAtom } from 'jotai';
import { navAtom } from 'atoms/navAtom';
import { SimpleDropdown } from 'utils/SimpleDropdown';
import { galleryData } from 'data/Gallery';
import { companyData } from 'data/Company';
import { IoChevronDown } from 'react-icons/io5';
import { AnimatePresence, motion } from 'framer-motion';
import GoogleRatingBanner from './GoogleRatingBanner';
import { useAuth } from '../contexts/AuthContext';

const logo =
  'https://res.cloudinary.com/dwoubdppz/image/upload/v1751597827/oak-and-stone-remodel/logo/logo.svg';

export const Nav = ({ full }) => {
  const [, setNav] = useAtom(navAtom);
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [open, setOpen] = useState(false);
  const [hamburgerIndex, setHamburgerIndex] = useState(-1);
  const navRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { currentUser, logout } = useAuth();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleUniqueNavClick = () => {
    scrollToTop();
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Handle scroll effect with throttling and better threshold handling
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Only update state if we've crossed the threshold significantly
          // This prevents the spasm effect near the threshold
          if (currentScrollY > 50 && !isScrolled) {
            setIsScrolled(true);
          } else if (currentScrollY < 10 && isScrolled) {
            setIsScrolled(false);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Close hamburger menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpen(false);
        setHamburgerIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close hamburger menu on screen resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setOpen(false);
        setHamburgerIndex(-1);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div
        ref={navRef}
        className={cn(
          'sticky top-0 z-50 w-full transition-all duration-300 nav-container',
          isScrolled ? 'bg-white shadow-md' : 'bg-[#F8F8F8]'
        )}>
        {/* Google Rating Banner */}
        <AnimatePresence initial={false} mode="wait">
          {!isScrolled && (
            <motion.div
              key="google-rating-banner"
              initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
              animate={{ opacity: 1, height: 'auto', overflow: 'visible' }}
              exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
              transition={{
                duration: 0.4,
                ease: [0.4, 0.0, 0.2, 1],
                height: { duration: 0.3 }
              }}>
              <GoogleRatingBanner />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="relative">
          <div className="flex items-center justify-between mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 xl:px-24">
            <Link
              to="/"
              onClick={scrollToTop}
              className={cn(
                'relative z-50 py-4 transition-all duration-300',
                isScrolled ? 'py-3' : 'py-5'
              )}>
              <img
                alt="oak and stone logo"
                src={logo}
                className={cn(
                  'h-8 transition-all duration-300',
                  full && 'h-10 xl:h-12'
                )}
              />
            </Link>

            <div className="hidden items-center md:flex">
              {navOptions.map((item, index) => {
                let isActive = location.pathname === item.link;
                // For Portfolio, active if path starts with /gallery
                if (
                  item.text === 'Portfolio' &&
                  location.pathname.startsWith('/gallery')
                ) {
                  isActive = true;
                }
                // For Company, active if path starts with /process, /about, /featured, or /careers
                if (
                  item.text === 'Company' &&
                  (location.pathname.startsWith('/process') ||
                    location.pathname.startsWith('/about') ||
                    location.pathname.startsWith('/featured') ||
                    location.pathname.startsWith('/careers'))
                ) {
                  isActive = true;
                }

                return (
                  <div
                    key={index}
                    className="relative group"
                    onMouseEnter={() => setActiveDropdown(index)}
                    onMouseLeave={() => setActiveDropdown(null)}>
                    {item.dropdown ? (
                      <button
                        onClick={() => {
                          setActiveDropdown(
                            activeDropdown === index ? null : index
                          );
                        }}
                        className={cn(
                          'flex items-center py-6 duration-200 font-medium tracking-wide whitespace-nowrap relative group/nav hover:bg-gray-50/50 rounded-lg transition-all duration-200 bg-transparent border-none cursor-pointer focus:outline-none focus:ring-0',
                          isScrolled && 'py-5',
                          isActive
                            ? 'text-[#2C3E50]'
                            : 'text-[#4A5568] hover:text-[#2C3E50]',
                          // Add extra right padding to Service Area
                          item.text === 'Service Area'
                            ? 'px-3 lg:px-10'
                            : 'px-3 lg:px-4'
                        )}>
                        {item.text}
                        <motion.div
                          animate={{
                            rotate: activeDropdown === index ? 180 : 0,
                            y: activeDropdown === index ? 1 : 0
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 25
                          }}
                          className="flex justify-center items-center ml-1.5">
                          <IoChevronDown className="text-gray-500 size-4" />
                        </motion.div>
                        <motion.div
                          className={cn(
                            'absolute bottom-0 left-0 right-0 h-[2px] rounded-full',
                            isActive
                              ? 'bg-gradient-to-r from-primary to-primary-hover'
                              : 'bg-gradient-to-r from-[#A0AEC0] to-[#CBD5E0]'
                          )}
                          initial={false}
                          animate={{
                            scaleX:
                              isActive || activeDropdown === index ? 1 : 0,
                            opacity:
                              isActive || activeDropdown === index ? 1 : 0,
                            transformOrigin: 'center'
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 25
                          }}
                        />
                      </button>
                    ) : (
                      <Link
                        to={item.link}
                        onClick={() => {
                          scrollToTop();
                          setNav(item.text);
                          setActiveDropdown(null);
                        }}
                        className={cn(
                          'flex items-center py-6 duration-200 font-medium tracking-wide whitespace-nowrap relative group/nav hover:bg-gray-50/50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-0',
                          isScrolled && 'py-5',
                          isActive
                            ? 'text-[#2C3E50]'
                            : 'text-[#4A5568] hover:text-[#2C3E50]',
                          // Add extra right padding to Service Area
                          item.text === 'Service Area'
                            ? 'px-3 lg:px-10'
                            : 'px-3 lg:px-4'
                        )}>
                        {item.text}
                        <motion.div
                          className={cn(
                            'absolute bottom-0 left-0 right-0 h-[2px] rounded-full',
                            isActive
                              ? 'bg-gradient-to-r from-primary to-primary-hover'
                              : 'bg-gradient-to-r from-[#A0AEC0] to-[#CBD5E0]'
                          )}
                          initial={false}
                          animate={{
                            scaleX:
                              isActive || activeDropdown === index ? 1 : 0,
                            opacity:
                              isActive || activeDropdown === index ? 1 : 0,
                            transformOrigin: 'center'
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 25
                          }}
                        />
                      </Link>
                    )}
                    <AnimatePresence>
                      {activeDropdown === index && item.dropdown && (
                        <div className="relative">
                          {React.cloneElement(item.dropdown, {
                            onItemClick: () => setActiveDropdown(null)
                          })}
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              <Link to="/contact">
                <button
                  onClick={scrollToTop}
                  className={cn(
                    'flex items-center whitespace-nowrap px-5 lg:px-6 rounded-md text-white font-medium transition-all duration-200',
                    isScrolled ? 'py-2.5' : 'py-3',
                    'bg-primary hover:bg-primary-hover',
                    'shadow-sm hover:shadow-md transform hover:-translate-y-0.5',
                    'focus:outline-none focus:ring-0 active:bg-primary-hover/90'
                  )}>
                  REQUEST CONSULTATION
                </button>
              </Link>

              {/* Admin Dashboard Link */}
              {currentUser && (
                <Link to="/admin" className="ml-4">
                  <button
                    onClick={scrollToTop}
                    className={cn(
                      'flex items-center whitespace-nowrap px-4 py-2 rounded-md text-white font-medium transition-all duration-200',
                      isScrolled ? 'py-1.5' : 'py-2',
                      'bg-gray-600 hover:bg-gray-700',
                      'shadow-sm hover:shadow-md transform hover:-translate-y-0.5',
                      'focus:outline-none focus:ring-0 active:bg-gray-700/90'
                    )}>
                    Admin Dashboard
                  </button>
                </Link>
              )}

              {/* Admin Welcome Message and Logout */}
              {currentUser && (
                <div className="flex items-center ml-4 space-x-3">
                  <span className="text-sm text-gray-600">
                    Welcome, {currentUser.email}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-sm underline text-primary hover:text-primary-hover">
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* Hamburger Menu */}
            <div className="md:hidden">
              <button
                onClick={() => setOpen(!open)}
                className={cn(
                  'flex relative flex-col justify-center items-center w-10 h-10 transition-all duration-300 focus:outline-none focus:ring-0',
                  isScrolled ? 'py-2' : 'py-3'
                )}>
                <div className="flex flex-col justify-between w-6 h-4">
                  <span
                    className={cn(
                      'w-full h-0.5 bg-[#4A5568] rounded-full transform transition-all duration-300',
                      open && 'rotate-45 translate-y-[7px]'
                    )}
                  />
                  <span
                    className={cn(
                      'w-full h-0.5 bg-[#4A5568] rounded-full transition-all duration-300',
                      open && 'opacity-0'
                    )}
                  />
                  <span
                    className={cn(
                      'w-full h-0.5 bg-[#4A5568] rounded-full transform transition-all duration-300',
                      open && '-rotate-45 -translate-y-[7px]'
                    )}
                  />
                </div>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {open && (
            <div className="absolute right-0 left-0 top-full rounded-b-xl border divide-y divide-gray-100 shadow-xl backdrop-blur-sm bg-white/95 md:hidden border-gray-200/80">
              {navOptionsHamburger.map((item, index) => (
                <React.Fragment key={index}>
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => {
                          if (item.dropdown) {
                            setHamburgerIndex(
                              hamburgerIndex === index ? -1 : index
                            );
                          } else {
                            // For non-dropdown items, navigate and close menu
                            handleUniqueNavClick();
                          }
                        }}
                        className="flex justify-between items-center p-4 mx-2 w-full text-left rounded-lg transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-0"
                        aria-expanded={hamburgerIndex === index}
                        aria-controls={`mobile-dropdown-${index}`}>
                        <span className="text-[#4A5568]">{item.text}</span>
                        <motion.div
                          animate={{
                            rotate: hamburgerIndex === index ? 180 : 0
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 200,
                            damping: 20
                          }}
                          className="flex justify-center items-center">
                          <IoChevronDown className="size-4 text-[#A0AEC0]" />
                        </motion.div>
                      </button>
                      {hamburgerIndex === index && (
                        <div
                          id={`mobile-dropdown-${index}`}
                          className="mx-2 rounded-b-lg border-t border-gray-100 bg-gray-50/80">
                          {item.dropdown.map((subItem, subIdx) => (
                            <Link
                              key={subIdx}
                              to={subItem.link}
                              className="block px-8 py-3 text-[#4A5568] hover:bg-gray-100 transition-colors duration-200 font-medium focus:outline-none focus:ring-0"
                              onClick={handleUniqueNavClick}>
                              {subItem.text}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.link}
                      className={cn(
                        'block p-4 transition-all duration-200 rounded-lg mx-2 font-medium focus:outline-none focus:ring-0',
                        item.text === 'Request Consultation'
                          ? 'bg-primary text-white hover:bg-primary-hover shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-semibold text-center mb-3'
                          : 'text-[#4A5568] hover:bg-gray-50'
                      )}
                      onClick={handleUniqueNavClick}>
                      {item.text}
                    </Link>
                  )}
                </React.Fragment>
              ))}

              {/* Admin Section in Mobile Menu - Only show if user is logged in */}
              {currentUser && (
                <div className="border-t border-gray-100">
                  <div className="p-4 text-sm text-gray-600 bg-gray-50/50">
                    Welcome, {currentUser.email}
                  </div>
                  <Link
                    to="/admin"
                    className="block p-4 text-[#4A5568] hover:bg-gray-50 transition-all duration-200 rounded-lg mx-2 font-medium focus:outline-none focus:ring-0"
                    onClick={handleUniqueNavClick}>
                    Admin Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full p-4 text-left text-[#4A5568] hover:bg-gray-50 transition-all duration-200 rounded-lg mx-2 font-medium focus:outline-none focus:ring-0">
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const navOptions = [
  {
    text: 'Portfolio',
    dropdown: <SimpleDropdown data={galleryData} />
  },
  {
    text: 'Company',
    dropdown: <SimpleDropdown data={companyData} />
  },
  {
    text: 'Service Area',
    link: '/service-area'
  }
];

// Refactored navOptionsHamburger to support sub-items for dropdowns
const navOptionsHamburger = [
  {
    text: 'Portfolio',
    dropdown: galleryData.map((item) => ({
      text: item.title,
      link: `/gallery/${item.slug}`
    }))
  },
  {
    text: 'Company',
    dropdown: companyData.map((item) => ({
      text: item.title,
      link: `/${item.slug}`
    }))
  },
  {
    text: 'Service Area',
    link: '/service-area'
  },
  {
    text: 'Request Consultation',
    link: '/contact'
  }
];
